/**
 * HET CUSTOM ELEMENT van SDK v1 — `<mark2form-configurator>`.
 * Structuurplan §5.2 stappen 2–9 en §8.2.
 *
 * Dit is de klantkant van het embed-protocol: het element dat een maker op
 * zijn eigen pagina zet, de iframe die het daaronder bouwt, de handshake die
 * het voert, de verificatie-ping waarmee het portaal hoort dat de embed écht
 * geladen is (stap 6), en de twee `CustomEvent`s waarmee het de pagina laat
 * weten dat de bezoeker klaar is.
 *
 * ── DE DRIE REGELS WAAR DIT BESTAND AAN VASTZIT ─────────────────────────────
 *
 * 1. GEEN DEPENDENCIES (§8.2). Dit wordt een IIFE-bundel (`sdk/v1.js`) die op
 *    een vreemde klantpagina draait, naast WordPress, jQuery 1.9, drie
 *    trackers en een cookiebanner. Geen framework, geen polyfill, geen `zod` —
 *    alleen de parser hiernaast en de contractmodules die zelf niets op
 *    runtime importeren: `@mark2form/contracts/embed-protocol` (de spelling),
 *    `@mark2form/contracts/endpoint-paden` (de paden, als data) en
 *    `@mark2form/contracts/canonical-json` (de vingerafdruk voor de
 *    stap-9-ontdubbeling). De tweede bestaat precies hiervoor: `./endpoints`
 *    bouwt op module-niveau zod-schema's, dus één import daarvan trekt zod de
 *    bundel in.
 *    `bundel-controle.mjs` faalt op elk overgebleven spoor.
 *
 * 2. DE IFRAME IS DE BEVEILIGINGSGRENS (§3 principe 6). Het element is een
 *    WRAPPER om een iframe, geen vervanging ervan — daarom is "web component
 *    als primaire embed, iframe alleen fallback" in het structuurplan
 *    afgewezen. Alles wat de configurator doet gebeurt in een ander document,
 *    op een andere origin, met een eigen CSP.
 *
 * 3. HERKOMST LEEST DE BROWSER, NIET DE AFZENDER. Elke binnenkomende
 *    `message` wordt op `event.source` én `event.origin` getoetst vóór de
 *    vorm. Zie `isVanOnzeIframe`.
 *
 * ── GEEN SHADOW DOM ─────────────────────────────────────────────────────────
 * Bewust licht DOM: het contract belooft `iframe[data-m2f]` (en de legacy
 * `iframe[data-upg]`) als "het haakje waarmee een klant er met eigen CSS op
 * selecteert". Een shadow root zou precies die selector onbereikbaar maken.
 *
 * ── SSR-VEILIG: DE KLASSE STAAT IN EEN FUNCTIE ──────────────────────────────
 * `class X extends HTMLElement` is een EXPRESSIE die `HTMLElement` op
 * module-niveau uitvoert. In Node (de SSR-bundel van `apps/runtime`, een
 * Vitest-run in node-omgeving, een Next-route die per ongeluk de barrel
 * importeert) bestaat dat globale niet en gooit de import een
 * `ReferenceError` — vóór er ook maar iets is aangeroepen. Daarom wordt de
 * klasse pas gebouwd wanneer je erom vraagt; importeren van deze module heeft
 * nul neveneffecten.
 */

import {
  CUSTOM_ELEMENT_NAAM,
  DATA_ATTRIBUUT,
  DATA_ATTRIBUUT_LEGACY,
  EMBED_BERICHTEN,
  EMBED_PLAATSING_QUERY,
  INIT_TIMEOUT_MS,
  PLAATSING_NIET_BESCHIKBAAR,
  WINKELWAGEN_FOUTCODES,
  WINKELWAGEN_MELDING_MAX,
  embedHulpcode,
  isOfferteId,
  isPlaatsingPubliekeId,
  isWinkelwagenModus,
  type EmbedDesignCompletePayload,
  type EmbedPlaatsingWeigercode,
} from "@mark2form/contracts/embed-protocol";
// De paden van `/api/v1` ZONDER zod — de projectie die precies voor deze
// bundel bestaat. `./endpoints` bouwt op module-niveau zod-schema's, dus één
// import daarvan (wélke export dan ook) trekt zod de IIFE in en breekt §8.2.
import { ENDPOINT_PADEN } from "@mark2form/contracts/endpoint-paden";
// De ENE canonieke serialisatie van het platform (C04: "geen eigen
// JSON.stringify-variant"), hier voor de vingerafdruk van een stap-9-payload.
// Nul imports, geen zod: veilig in de IIFE.
import { canoniekeJson } from "@mark2form/contracts/canonical-json";

import { klemEmbedHoogte, parseEmbedBericht, type EmbedCorrelatie } from "./embed-protocol-parser";

/* ══════════════════════════════════════════════════════════════════════════
   WAARDEN DIE (NOG) NIET IN CONTRACTS STAAN
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * De starthoogte uit §5.2 stap 3 (`min-height:560px`) — wat de bezoeker ziet
 * in de milliseconden vóór het eerste `resize`-bericht.
 *
 * Dit is NIET `HOOGTE_MIN_PX` (200): die is de ondergrens van de KLEM op een
 * binnengekomen meting, en 200 px als startbeeld zou elke embed laten
 * "opspringen". Twee waarden, twee betekenissen.
 *
 * Hij staat hier en niet in contracts omdat contracts hem vandaag niet draagt;
 * zodra de runtime dezelfde 560 nodig heeft is dat een contractregel waard —
 * genoteerd in het verslag bij deze PR.
 */
const START_HOOGTE_PX = 560;

/**
 * De debounce op stap 8, in ms. De zender in de iframe debounced al 100 ms
 * (`use-iframe-resize.ts`); deze tweede debounce beschermt de KLANTPAGINA
 * tegen een iframe die dat niet doet — een oudere runtime, een andere
 * productmodule, of straks de Shopify-app. Layout-thrash op een pagina die wij
 * niet beheren is het duurste wat een embed kan doen.
 */
const RESIZE_DEBOUNCE_MS = 100;

/**
 * Hoe lang na een stap-9-bericht zijn TWEELING nog als kopie telt, in ms.
 *
 * ── WAAROM ER TWEELINGEN ZIJN ───────────────────────────────────────────────
 * De zender in de iframe (`emitDesignComplete` en `emitRequestClose` in
 * `packages/configurator-engine/src/use-iframe-resize.ts`) stuurt elk
 * stap-9-bericht in BEIDE spellingen, direct na elkaar en met dezelfde
 * payload: eerst `m2f:*`, dan `upgraded:*` — zodat een oude loader het ook
 * hoort. Dit element leest beide spellingen (een oude runtime stuurt alleen de
 * legacy-naam), dus zonder ontdubbeling kreeg de klant `m2f:design-complete`
 * twee keer. Voor een checkout is dat een dubbele winkelwagenregel.
 *
 * ── HET MECHANISME ──────────────────────────────────────────────────────────
 * Per element en per soort (DESIGN_COMPLETE, REQUEST_CLOSE) onthouden we het
 * laatst afgeleverde bericht: welke spelling, de vingerafdruk van de payload
 * (`canoniekeJson`, dus onafhankelijk van de sleutelvolgorde) en wanneer.
 * Komt daarna een bericht van DEZELFDE soort in de ANDERE spelling met een
 * GELIJKE vingerafdruk binnen dit venster, dan is het de tweeling: overslaan,
 * en het spoor wissen (een tweeling wordt maar één keer opgegeten). Al het
 * andere gaat door en wordt het nieuwe spoor:
 *
 *   - alleen legacy (een oude runtime)       → nooit een tweeling → 1 event
 *   - m2f, later nog een m2f (nieuw ontwerp) → zelfde spelling  → 2 events
 *   - m2f(A), legacy(B)                      → andere payload   → 2 events
 *
 * "Dezelfde bron-iframe" is gratis: het spoor hangt aan het element, en het
 * element accepteert alleen berichten van zijn eigen iframe
 * (`isVanOnzeIframe`). Bij een nieuwe iframe (`ruimOp`) begint het spoor leeg.
 *
 * De vergelijking is symmetrisch: een zender die de legacy-naam EERST stuurt,
 * wordt net zo goed ontdubbeld. Het eerste bericht wint, en de detail is in
 * beide gevallen dezelfde (de parser normaliseert de spelling).
 *
 * ── WAAROM EEN TIJDVENSTER, EN WAAROM 1000 MS ───────────────────────────────
 * De twee `postMessage`s vertrekken in één taak, maar komen hier als twee
 * losse taken binnen; daartussen kan de klantpagina andere taken draaien (een
 * tracker, een lange layout). "Dezelfde taak" is aan deze kant dus niet te
 * zien — een tijdgrens wel. Die grens is ruim, want te kort kost een dubbel
 * event, en te lang kost niets: een ANDERE zender die binnen één seconde
 * dezelfde payload in de andere spelling stuurt, bestaat niet. Het venster
 * vangt alleen een verouderd spoor, bijvoorbeeld een iframe die na een
 * rollback terugvalt op een runtime die alleen de legacy-naam kent.
 */
const TWEELING_VENSTER_MS = 1000;

/** De stap-9-soorten die in twee spellingen kunnen binnenkomen. */
type Stap9Soort = "DESIGN_COMPLETE" | "REQUEST_CLOSE";

/** Het laatst afgeleverde stap-9-bericht van één soort. */
interface Stap9Spoor {
  readonly legacy: boolean;
  readonly vingerafdruk: string;
  readonly tijd: number;
}

/**
 * De vingerafdruk van een payload, of `null` als die niet te maken is (een
 * `Date`, een kringverwijzing: `postMessage` laat meer toe dan JSON). Zonder
 * vingerafdruk geen ontdubbeling — dan gaat het bericht gewoon door, zoals
 * vóór deze regel. Liever een dubbel event dan een verloren event.
 */
function vingerafdrukVan(payload: unknown): string | null {
  try {
    return canoniekeJson(payload);
  } catch {
    return null;
  }
}

/**
 * Waar de iframe vandaan komt (§5.2 stap 3:
 * `https://embed.mark2form.com/c/<product>`).
 *
 * LET OP: dit domein is er vandaag nog NIET — de runtime staat op
 * `upgraded.tools` en §8.1 zet `embed.mark2form.com` als primair domein met
 * `upgraded.tools` als alias. De constante volgt daarom het PLAN, en elke
 * embed kan hem overrulen:
 *
 *   - per element : `<mark2form-configurator base="https://upgraded.tools">`
 *   - per pagina  : `zetEmbedBasis('https://upgraded.tools')` vóór registratie
 *
 * Zonder die uitweg zou de SDK pas bruikbaar zijn na een DNS-wijziging, en dat
 * is precies de koppeling die je niet wilt tussen een bundel en een domein.
 */
export const STANDAARD_EMBED_BASIS = "https://embed.mark2form.com";

let embedBasis = STANDAARD_EMBED_BASIS;

/**
 * Zet het basisdomein voor elke embed op deze pagina. Zie
 * `STANDAARD_EMBED_BASIS`.
 *
 * Gooit op alles wat geen `http(s)`-URL is. Dat is met opzet luid: deze
 * functie wordt door de PAGINA-AUTEUR aangeroepen, niet door een bezoeker, en
 * een stil genegeerde instelling levert een embed op die op het verkeerde
 * domein laadt — een fout die je pas ziet als een klant belt. `http:` blijft
 * toegestaan voor `http://localhost:3000` tijdens ontwikkeling.
 */
export function zetEmbedBasis(url: string): void {
  const kandidaat = normaliseerBasis(url);
  if (!isHttpBasis(kandidaat)) {
    throw new TypeError(`zetEmbedBasis: "${url}" is geen http(s)-URL.`);
  }
  embedBasis = kandidaat;
}

/** Het basisdomein dat nu geldt (zonder `base`-attribuut op het element). */
export function huidigeEmbedBasis(): string {
  return embedBasis;
}

function normaliseerBasis(url: string): string {
  return url.trim().replace(/\/+$/, "");
}

/**
 * Alleen `http(s)` telt als basis. Niet uit achterdocht jegens de klant — die
 * schrijft zijn eigen pagina en kan daar toch alles — maar omdat de basis
 * rechtstreeks in `iframe.src` belandt: een `javascript:`- of `data:`-URL zou
 * daar SCRIPT worden in het document van de klant, en een SDK hoort geen weg
 * te bieden waarlangs een verdwaald attribuut dat wordt.
 */
function isHttpBasis(url: string): boolean {
  try {
    const protocol = new URL(url).protocol;
    return protocol === "https:" || protocol === "http:";
  } catch {
    return false;
  }
}

/* ══════════════════════════════════════════════════════════════════════════
   HET PORTAAL (§5.2 stap 6)
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Waar de verificatie-ping van stap 6 naartoe gaat.
 *
 * Dit is een ANDER adres dan `STANDAARD_EMBED_BASIS`: de iframe komt van de
 * runtime (`embed.mark2form.com`), de API van het portaal
 * (`app.mark2form.com`, §5.3 + §8.1) — hetzelfde domein waar `sdk/v1.js` zelf
 * vandaan komt (§5.2 stap 1). Twee domeinen, twee constanten; ze uit elkaar
 * afleiden zou de eerste keer stukgaan dat er één verhuist.
 *
 * Net als de embed-basis volgt deze constante het PLAN en is hij te overrulen
 * met `zetPortaalBasis()` — zie het docblok daar.
 *
 * ── K33: WELK PORTAAL, IN DEZE VOLGORDE ────────────────────────────────────
 *  1. het attribuut `portal="…"` op het element (als het een http(s)-URL is);
 *  2. de paginabrede waarde: `zetPortaalBasis()`, en in `sdk/v1.js` standaard
 *     het portaal dat het script serveerde (`packages/embed/src/sdk-iife.ts`);
 *  3. deze constante.
 * Zo landt de ping van een preview of een tweede merk bij dát portaal en niet
 * in productie.
 */
export const STANDAARD_PORTAAL_BASIS = "https://app.mark2form.com";

let portaalBasis = STANDAARD_PORTAAL_BASIS;

/**
 * Zet het portaaladres voor deze pagina (waar de verificatie-ping heen gaat).
 *
 * Gooit op alles wat geen `http(s)`-URL is, om dezelfde reden als
 * `zetEmbedBasis`: een pagina-auteur roept dit aan, en een stil genegeerde
 * instelling levert pings op die nergens aankomen.
 */
export function zetPortaalBasis(url: string): void {
  const kandidaat = normaliseerBasis(url);
  if (!isHttpBasis(kandidaat)) {
    throw new TypeError(`zetPortaalBasis: "${url}" is geen http(s)-URL.`);
  }
  portaalBasis = kandidaat;
}

/** Het portaaladres dat nu geldt. */
export function huidigePortaalBasis(): string {
  return portaalBasis;
}

/**
 * De volledige URL van de verificatie-ping.
 *
 * Het PAD komt uit `ENDPOINT_PADEN` en wordt hier niet overgetypt. Bewust de
 * `pad`-kolom (`/api/v1/embed/verify`) en niet de `alias`: de alias is het pad
 * van het Astro-portaal dat vandaag live staat, en dat portaal kent deze SDK niet
 * — `sdk/v1.js` wordt door het NIEUWE portaal geserveerd en dat serveert de
 * v1-paden. Waar de serverkant (`./endpoint-pad`) met `PORTAL_API_V1` tussen de
 * twee kolommen kan schakelen, heeft een browser geen environment om te lezen;
 * wie toch naar een ander portaal wil wijst met `portal="…"` of
 * `zetPortaalBasis()` en krijgt daar hetzelfde v1-pad.
 *
 * K33: de basis is een parameter — het element kiest hem
 * (`portaalVoorElement`), niet de module.
 */
function verifyUrl(basis: string): string {
  return `${basis}${ENDPOINT_PADEN["embed-verify"].pad}`;
}

/**
 * Welke (portaal, gid)-paren deze PAGELOAD al gemeld heeft.
 *
 * §5.2 stap 6 zegt "eenmalig per pageload", en dat is hier letterlijk
 * genomen: de verzameling staat op MODULE-niveau, niet op het element. Twee
 * `<mark2form-configurator>`-elementen met dezelfde `gid` sturen samen één
 * ping, en een tweede `ready` — een re-render, een `attributeChangedCallback`
 * die de iframe herbouwt, een iframe die opnieuw laadt — voegt er geen toe.
 *
 * De sleutel draagt de gid ÉN de URL: de ping gaat over één grant, dus twee
 * elementen met verschillende `gid` zijn twee verschillende registraties en
 * horen allebei door te gaan. Alleen een echte pageload wist deze lijst; dat
 * is precies de bedoeling.
 */
const GEVERIFIEERD = new Set<string>();

/* ══════════════════════════════════════════════════════════════════════════
   DE PUBLIEKE GEBEURTENISSEN (§5.2 stap 9) EN FOUTCODES
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * De DOM-gebeurtenissen die het element op zichzelf dispatcht.
 *
 * ── WAAROM DE BERICHTNAAM ZELF DE EVENTNAAM IS ──────────────────────────────
 * `m2f:design-complete` op de draad wordt `m2f:design-complete` op het
 * element. Een eigen DOM-vocabulaire ernaast (`mark2formDesignComplete`,
 * `design-complete`, …) zou een TWEEDE naamlijst zijn voor hetzelfde
 * gebeuren — precies de drift die §3 principe 3 verbiedt. Nu hoeft een klant
 * maar één naam te kennen, en komt die uit contracts.
 *
 * `FOUT` is de uitzondering: dat is geen protocolbericht maar de manier waarop
 * het element een eindstand meldt die de klantpagina moet kunnen zien (§5.2
 * stap 5 noemt `embed-init-timeout` met zoveel woorden). Een `console.error`
 * is geen API; een event wel.
 *
 * Alle drie bubbelen (`bubbles: true`) zodat een klant één listener op
 * `document` kan zetten in plaats van één per element, en `composed: true`
 * zodat het ook werkt wanneer de klant het element zélf in een shadow root
 * hangt.
 */
export const SDK_EVENTS = {
  DESIGN_COMPLETE: EMBED_BERICHTEN.DESIGN_COMPLETE.m2f,
  REQUEST_CLOSE: EMBED_BERICHTEN.REQUEST_CLOSE.m2f,
  /**
   * Shopkoppelingen (WP K3b) — de bezoeker wil het ontwerp in de winkelwagen
   * van de PAGINA. Annuleerbaar: de pagina mag `preventDefault()` zeggen als zij
   * het zelf afhandelt; de SDK kijkt er niet naar. Het antwoord gaat terug met
   * `winkelwagenUitkomst()`.
   */
  ADD_TO_CART: EMBED_BERICHTEN.ADD_TO_CART.m2f,
  FOUT: "m2f:error",
} as const;

/**
 * De foutcode als het antwoord van de pagina op `winkelwagenUitkomst()` niet
 * klopt met het contract (`WinkelwagenUitkomstSchema`). Hij staat BUITEN
 * `SDK_FOUTCODES` omdat `EMBED_HULPCODES` (contracts) er nog geen hulpcode voor
 * heeft: de melding krijgt dan `E100` ("onbekend"). Die hulpcode toevoegen is een
 * contractwijziging en staat als open punt in het WP K3b-rapport.
 */
const FOUT_ONGELDIG_CART_RESULT = "embed-invalid-cart-result" as const;

/**
 * De codes in `detail.code` van een `m2f:error`.
 *
 * `embed-init-timeout` is dezelfde eindstand als in `SessionEndedReason`
 * (`embed-session-client.ts`) en in §5.2 stap 5. Die union is een TYPE, geen
 * waarde, dus hij is hier niet te importeren — de string staat daarom letterlijk
 * hier.
 *
 * DIT IS DE LAATSTE TABEL VAN DIT BESTAND DIE NOG NIET UIT CONTRACTS KOMT.
 * Issue #56 leverde de payload van stap 9 en de zod-vrije padprojectie; de
 * foutcodes bleven achter. Zodra een tweede kant ze nodig heeft (de runtime die
 * dezelfde eindstand meldt, of het portaal dat er in de koppelwizard op
 * filtert) horen ze naar `@mark2form/contracts/embed-protocol` te verhuizen en
 * verdwijnt deze tabel daarheen. Zolang de SDK de enige zender is, is één
 * plek ook precies één plek.
 */
export const SDK_FOUTCODES = {
  /** Geen `ready` binnen INIT_TIMEOUT_MS na het laden van de iframe. */
  INIT_TIMEOUT: "embed-init-timeout",
  /** `product` ontbreekt — er is geen URL te bouwen. */
  GEEN_PRODUCT: "embed-missing-product",
  /** Noch `key` noch `gid` — er is geen sessiepad (§5.2 stap 5). */
  GEEN_SLEUTEL: "embed-missing-credential",
  /**
   * K05a — het attribuut `placement` staat er, maar is geen publieke
   * plaatsing-id (`pl_` + 16–32 kleine letters/cijfers, het patroon uit
   * contracts). Er komt dan GEEN iframe: een embed die half bij een plaatsing
   * hoort, bestaat niet.
   */
  ONGELDIGE_PLAATSING: "embed-invalid-placement",
  /**
   * K05a — de plaatsing draait niet. Twee momenten:
   *   - de READY zegt onze instance en plaatsing niet terug (een runtime die
   *     `placement` niet kent): dan gaat er geen INIT uit, dus ook geen sleutel;
   *   - het PLAATSING-bericht van de iframe meldt een weigering (het portaal
   *     gaf geen sessie).
   * De code komt uit contracts (`EMBED_PLAATSING_WEIGERCODES`): de iframe meldt
   * dezelfde.
   */
  PLAATSING_NIET_BESCHIKBAAR,
} as const;

/**
 * Elke code die in `detail.code` kan staan. De weigercodes van contracts horen
 * er als geheel bij: K03 mag er een toevoegen, en de runtime die hem stuurt is
 * dan de bron, niet deze tabel.
 */
export type SdkFoutcode =
  | (typeof SDK_FOUTCODES)[keyof typeof SDK_FOUTCODES]
  | EmbedPlaatsingWeigercode
  | typeof FOUT_ONGELDIG_CART_RESULT;

/**
 * `detail` van `m2f:design-complete` — letterlijk het payloadtype van het
 * contract (`EmbedDesignCompletePayload`, issue #56).
 *
 *     document.addEventListener("m2f:design-complete", (e) => {
 *       e.detail.design_id;              // string
 *       e.detail.total_price?.amount;    // number, in de HOOFDEENHEID
 *       e.detail.total_price?.currency;  // "EUR" | "GBP" | …
 *     });
 *
 * ── `total_price` IS GENEST ─────────────────────────────────────────────────
 * `{amount, currency}` en niet twee losse velden. Plat zou een BEDRAG ZONDER
 * MUNT toelaten, en dan neemt de ontvanger euro's aan — precies de aanname die
 * de eigenaar verwierp, alleen aan deze kant van de lijn. Genest is die stand
 * onmogelijk: wie een prijs stuurt, stuurt allebei de velden. `currency` is de
 * gesloten `Currency`-unie, geen vrije string.
 *
 * Het bedrag is een HAAKJE voor een checkout-flow, nooit de bron van waarheid
 * voor een factuur: het komt via `postMessage` van een pagina die wij niet
 * beheren en is dus aanvallersinvoer. Reken af op de server.
 *
 * ── WAT ER NAAST DEZE VELDEN NOG IN KAN ZITTEN ──────────────────────────────
 * Velden die het contract (nog) niet kent reizen op RUNTIME plat mee, zodat een
 * nieuwere iframe naast een oudere `v1.js` niets verliest (§8.2: minors zijn
 * additief en landen op hetzelfde pad). Ze staan bewust NIET in dit type: voor
 * wie leest is het contract de poort, en een veld dat er niet in staat, staat
 * er niet.
 */
export type DesignCompleteDetail = EmbedDesignCompletePayload;

/** `detail` van `m2f:error`. */
export interface FoutDetail {
  readonly code: SdkFoutcode;
  readonly bericht: string;
}

/* ══════════════════════════════════════════════════════════════════════════
   HET ELEMENT
   ══════════════════════════════════════════════════════════════════════════ */

/** De publieke vorm van `<mark2form-configurator>`. */
export interface Mark2FormConfiguratorElement extends HTMLElement {
  /** De geïnjecteerde iframe, of `null` zolang het element niet in de DOM hangt. */
  readonly iframe: HTMLIFrameElement | null;
  /** Heeft de handshake (`ready` → `init`) plaatsgevonden? */
  readonly handshakeVoltooid: boolean;
  /**
   * K05a — de instance-id van de huidige iframe (crypto-random, per iframe
   * nieuw), of `null` zonder iframe. Hij gaat alleen over de draad als het
   * element een `placement` heeft. Een correlatie, geen geheim.
   */
  readonly instanceId: string | null;
  /**
   * K05a — de plaatsingsversie die de iframe na de sessie-uitwisseling
   * bevestigde (PLAATSING-bericht), of `null`. Na een weigering (ook een die
   * pas later komt, zoals een geweigerde verlenging) weer `null`.
   */
  readonly plaatsingVersie: number | null;
  /**
   * Shopkoppelingen (WP K3b) — geeft het antwoord van de pagina op een
   * `m2f:add-to-cart` door aan de configurator (`m2f:cart-result`), naar de
   * iframe-origin. `false` als het antwoord niet klopt met
   * `WinkelwagenUitkomstSchema` (dan ook een `m2f:error`) of als er geen iframe
   * is. Het antwoord wordt NIET op de prijs of het token gecontroleerd: de pagina
   * is de winkel, en de configurator toont alleen wat er terugkomt.
   */
  winkelwagenUitkomst(uitkomst: unknown): boolean;
}

/** De constructor die `customElements.define` krijgt. */
export type Mark2FormConfiguratorConstructor = new () => Mark2FormConfiguratorElement;

/**
 * De attributen van §5.2 stap 2, plus de twee die de SDK zelf toevoegt.
 * `cart` (WP K3b) hoort erbij: hij zit in het init-bericht, dus een wissel
 * betekent een nieuwe iframe, net als `key` en `gid`.
 */
const ATTRIBUTEN = ["product", "mode", "key", "gid", "base", "embed-title", "placement", "cart"] as const;

let klasseCache: Mark2FormConfiguratorConstructor | null = null;

/**
 * Bouwt (en onthoudt) de elementklasse. Aanroepen mag alleen in een browser:
 * `HTMLElement` moet bestaan. Zie het SSR-blok bovenaan dit bestand.
 */
export function maakConfiguratorElementKlasse(): Mark2FormConfiguratorConstructor {
  if (klasseCache !== null) return klasseCache;
  if (typeof HTMLElement === "undefined") {
    throw new Error(
      "maakConfiguratorElementKlasse() vraagt een DOM: HTMLElement bestaat hier niet.",
    );
  }

  class Mark2FormConfigurator extends HTMLElement {
    static get observedAttributes(): readonly string[] {
      return ATTRIBUTEN;
    }

    private iframeEl: HTMLIFrameElement | null = null;
    /** Het origin van de iframe — de enige afzender die we accepteren. */
    private iframeOrigin = "";
    private handshake = false;
    private initVerstuurd = false;
    private initTimer: ReturnType<typeof setTimeout> | null = null;
    private resizeTimer: ReturnType<typeof setTimeout> | null = null;
    private laatsteHoogte = 0;
    /** Per stap-9-soort het laatst afgeleverde bericht — zie `TWEELING_VENSTER_MS`. */
    private readonly stap9Spoor = new Map<Stap9Soort, Stap9Spoor>();
    /**
     * K05a — ALLE plaatsingstate staat hier, op het element. Er is geen
     * modulestate voor een instance: twee elementen op één pagina delen niets.
     *
     *   `plaatsingId`      het gekeurde `placement`-attribuut, of `null`
     *   `eigenInstanceId`  per iframe nieuw (`maakInstanceId`)
     *   `plaatsingStand`   per iframe één keer:
     *                        wacht ─READY met echo→ verbonden (INIT is weg)
     *                        verbonden ─PLAATSING→ bevestigd | geweigerd
     *                        wacht ─READY zonder echo→ geweigerd (geen INIT)
     */
    private plaatsingId: string | null = null;
    private eigenInstanceId: string | null = null;
    private plaatsingStand: "wacht" | "verbonden" | "bevestigd" | "geweigerd" = "wacht";
    private bevestigdeVersie: number | null = null;
    private onBericht: ((event: MessageEvent) => void) | null = null;
    private onLoad: (() => void) | null = null;

    get iframe(): HTMLIFrameElement | null {
      return this.iframeEl;
    }

    get handshakeVoltooid(): boolean {
      return this.handshake;
    }

    get instanceId(): string | null {
      return this.eigenInstanceId;
    }

    get plaatsingVersie(): number | null {
      return this.bevestigdeVersie;
    }

    connectedCallback(): void {
      // Dubbele bescherming tegen twee iframes in één element: een
      // `connectedCallback` zonder voorafgaande `disconnectedCallback` bouwt
      // niets nieuws.
      //
      // WAT DIT NIET REDT: een element VERPLAATSEN naar een andere ouder vuurt
      // eerst `disconnectedCallback` en dan `connectedCallback`, dus de embed
      // begint opnieuw en de bezoeker is zijn ontwerp kwijt. Dat is geen keuze
      // van deze code maar van het platform — een iframe die van ouder
      // wisselt HERLAADT sowieso, in elke browser. Wie de embed wil kunnen
      // verplaatsen, verplaatst de ouder.
      if (this.iframeEl !== null) return;
      this.bouw();
    }

    disconnectedCallback(): void {
      this.ruimOp();
    }

    attributeChangedCallback(naam: string, oud: string | null, nieuw: string | null): void {
      if (oud === nieuw) return;
      // Vóór de eerste mount valt er niets te herbouwen.
      if (this.iframeEl === null) return;
      // `key` en `gid` zijn al door de handshake gegaan; ze wisselen betekent
      // een andere sessie, en dat is een nieuwe iframe. `product`, `mode`,
      // `base` en `placement` zitten in de URL, dus die ook. `embed-title` is
      // cosmetisch.
      if (naam === "embed-title") {
        this.iframeEl.title = this.titel();
        return;
      }
      this.ruimOp();
      this.bouw();
    }

    /* ─── stap 3: de iframe ───────────────────────────────────────────── */

    private bouw(): void {
      const product = (this.getAttribute("product") ?? "").trim();
      if (product === "") {
        this.meldFout(
          SDK_FOUTCODES.GEEN_PRODUCT,
          `<${CUSTOM_ELEMENT_NAAM}> heeft een attribuut \`product\` nodig.`,
        );
        return;
      }

      // K05a — `placement` wordt STRIKT gekeurd, vóór er iets gebouwd wordt.
      const plaatsing = this.keurPlaatsingAttribuut();
      if (plaatsing === false) return;

      // Per iframe een eigen instance-id. Zonder `crypto` is een plaatsing
      // niet te correleren: dan liever geen iframe dan een halve sessie.
      const instance = maakInstanceId(this.defaultView());
      if (plaatsing !== null && instance === null) {
        this.meldFout(
          SDK_FOUTCODES.PLAATSING_NIET_BESCHIKBAAR,
          `<${CUSTOM_ELEMENT_NAAM}> kan hier geen instance-id maken (geen crypto.getRandomValues).`,
        );
        return;
      }
      this.plaatsingId = plaatsing;
      this.eigenInstanceId = instance;

      // Een `base` die geen http(s)-URL is wordt genegeerd (zie `isHttpBasis`);
      // `embedBasis` is dat per constructie altijd wél.
      const gevraagd = normaliseerBasis(this.getAttribute("base") ?? "");
      const basis = isHttpBasis(gevraagd) ? gevraagd : embedBasis;
      const src = this.bouwSrc(basis, product);
      this.iframeOrigin = leesOrigin(basis);

      const iframe = this.ownerDocument.createElement("iframe");
      iframe.src = src;
      iframe.title = this.titel();
      // Beide namen: `data-m2f` is v1, `data-upg` is wat klant-CSS vandaag
      // selecteert. De legacy-naam komt ERNAAST, nooit in de plaats.
      iframe.setAttribute(DATA_ATTRIBUUT, product);
      iframe.setAttribute(DATA_ATTRIBUUT_LEGACY, product);
      iframe.setAttribute("allow", "clipboard-write; fullscreen");
      iframe.setAttribute("loading", "lazy");
      iframe.style.display = "block";
      iframe.style.width = "100%";
      iframe.style.border = "0";
      iframe.style.minHeight = `${START_HOOGTE_PX}px`;

      // Een custom element is standaard `display:inline` — dan doet
      // `width:100%` op de iframe niets. Alleen zetten als de klant zelf niets
      // heeft opgegeven, zodat een eigen `display:flex` blijft staan.
      if (this.style.display === "") this.style.display = "block";

      this.iframeEl = iframe;
      this.luister();
      this.appendChild(iframe);
    }

    private bouwSrc(basis: string, product: string): string {
      const query: string[] = [];
      const gid = (this.getAttribute("gid") ?? "").trim();
      if (gid !== "") query.push(`gid=${encodeURIComponent(gid)}`);

      // De PRODUCTMODUS (`logo|tekst|auto`, §5.2 stap 2) — niet te verwarren
      // met de `mode` van het init-bericht (`key|cookie`, stap 5). Hij reist
      // in de URL mee omdat het init-bericht zijn eigen `mode` al heeft en
      // twee betekenissen in één veld een bug is die je pas in productie ziet.
      //
      // OPEN PUNT: de runtime leest `?mode=` vandaag nog niet (`/c/[slug]`
      // kent alleen `key` en `gid`). Een onbekende queryparameter is
      // onschadelijk — de embed werkt gewoon in zijn eigen standaardmodus —
      // maar de runtime-kant is nog fase-3-werk. Zie het verslag bij deze PR.
      const modus = (this.getAttribute("mode") ?? "").trim();
      if (modus !== "") query.push(`mode=${encodeURIComponent(modus)}`);

      // K05a — ACHTERAAN en alleen met een plaatsing, zodat de URL van een
      // snippet zonder `placement` byte-gelijk blijft. Waarom in de URL en niet
      // in de INIT: zie `EMBED_PLAATSING_QUERY` in contracts.
      if (this.plaatsingId !== null && this.eigenInstanceId !== null) {
        query.push(`${EMBED_PLAATSING_QUERY.PLAATSING}=${encodeURIComponent(this.plaatsingId)}`);
        query.push(`${EMBED_PLAATSING_QUERY.INSTANCE}=${encodeURIComponent(this.eigenInstanceId)}`);
        // De eigenaar opent zelf een korte proef-URL. Het fragment verlaat de
        // klantserver niet; alleen dit specifieke iframe draagt het ticket
        // door naar de beveiligde gid-exchange. Een publieke pl_-id doet dit niet.
        const fragment = this.defaultView()?.location.hash.replace(/^#/, "") ?? "";
        const proef = new URLSearchParams(fragment).get("m2f-proef");
        if (proef && /^pt_[A-Za-z0-9_-]{43}$/.test(proef)) query.push(`proef=${encodeURIComponent(proef)}`);
      }

      const pad = `${basis}/c/${encodeURIComponent(product)}`;
      return query.length === 0 ? pad : `${pad}?${query.join("&")}`;
    }

    /**
     * K05a — het `placement`-attribuut: `null` als het er niet staat, de id als
     * hij voldoet aan het contractpatroon, en `false` (met een `m2f:error`)
     * voor al het andere — ook een leeg attribuut en een id met een spatie.
     * Geen `trim()`, anders dan bij de andere attributen: een plaatsing
     * bepaalt wat de bezoeker mag, dus "bijna goed" is fout.
     */
    private keurPlaatsingAttribuut(): string | null | false {
      if (!this.hasAttribute("placement")) return null;
      const waarde = this.getAttribute("placement");
      if (isPlaatsingPubliekeId(waarde)) return waarde;
      this.meldFout(
        SDK_FOUTCODES.ONGELDIGE_PLAATSING,
        `<${CUSTOM_ELEMENT_NAAM}> heeft een ongeldige \`placement\`: verwacht \`pl_\` gevolgd door 16 tot 32 kleine letters of cijfers.`,
      );
      return false;
    }

    private titel(): string {
      const eigen = (this.getAttribute("embed-title") ?? "").trim();
      if (eigen !== "") return eigen;
      const product = (this.getAttribute("product") ?? "").trim();
      return product === "" ? "Configurator" : `Configurator — ${product}`;
    }

    /* ─── stap 4–5: de handshake ──────────────────────────────────────── */

    private luister(): void {
      const iframe = this.iframeEl;
      if (iframe === null) return;

      this.onBericht = (event: MessageEvent): void => this.verwerk(event);
      this.defaultView()?.addEventListener("message", this.onBericht);

      // DE TIMER START OP `load`, NIET OP `connectedCallback`.
      //
      // INIT_TIMEOUT_MS (1500) is het venster van de HANDSHAKE, niet van het
      // netwerk: in §5.2 stap 5 telt de iframe hem af NADAT hij zijn `ready`
      // heeft gestuurd. Zou het element hem bij het aanmaken starten, dan
      // meldt elke bezoeker op een trage verbinding een `embed-init-timeout`
      // terwijl er niets mis is — en met `loading="lazy"` begint de iframe
      // sowieso pas te laden wanneer hij in beeld komt.
      this.onLoad = (): void => {
        if (this.handshake) return;
        this.initTimer = setTimeout(() => {
          this.initTimer = null;
          if (this.handshake) return;
          this.meldFout(
            SDK_FOUTCODES.INIT_TIMEOUT,
            `Geen \`${EMBED_BERICHTEN.READY.m2f}\` binnen ${INIT_TIMEOUT_MS} ms na het laden van de embed.`,
          );
        }, INIT_TIMEOUT_MS);
      };
      iframe.addEventListener("load", this.onLoad);
    }

    /**
     * De HERKOMSTCONTROLE — twee eisen, allebei door de browser gezet.
     *
     * `event.source === iframe.contentWindow` sluit elk ander frame uit (een
     * advertentie-iframe, een chatwidget, de pagina zelf). `event.origin`
     * sluit uit dat onze eigen iframe naar een andere site navigeert en van
     * daaruit verder praat. De parent KENT het verwachte origin — hij heeft de
     * `src` zelf gezet — dus hier is een echte origin-check mogelijk, anders
     * dan aan de iframe-kant waar de klantorigin vooraf onbekend is.
     */
    private isVanOnzeIframe(event: MessageEvent): boolean {
      const iframe = this.iframeEl;
      if (iframe === null) return false;
      if (event.source === null || event.source !== iframe.contentWindow) return false;
      if (this.iframeOrigin !== "" && event.origin !== this.iframeOrigin) return false;
      return true;
    }

    private verwerk(event: MessageEvent): void {
      if (!this.isVanOnzeIframe(event)) return;

      const bericht = parseEmbedBericht(event.data);
      if (bericht === null) return;

      // K05a — DE DERDE EIS. Draagt een bericht een instance-id, dan moet het
      // de onze zijn; anders is het voor een ander element (of een verdwaald
      // bericht van een vorige iframe) en doen we niets. Zonder plaatsing
      // blijft een bericht zonder instance-id door de twee eisen van
      // `isVanOnzeIframe` gedekt: dat is elke embed van vandaag.
      const instance = bericht.correlatie?.instance_id;
      if (instance !== undefined && instance !== this.eigenInstanceId) return;
      // Mét plaatsing eist ELK bericht onze instance-id. De READY gaat hier nog
      // door: `keurReady` meldt een READY zonder echo als fout (een runtime die
      // `placement` niet kent), in plaats van hem stil te laten wegvallen.
      if (this.plaatsingId !== null && instance === undefined && bericht.naam !== "READY") return;

      switch (bericht.naam) {
        case "READY":
          // K05a — met een plaatsing gaat de INIT (en dus de sleutel) pas de
          // deur uit als de iframe DEZE plaatsing bevestigt.
          if (this.plaatsingId !== null && !this.keurReady(bericht.correlatie)) return;
          this.stuurInit(); // stap 5
          this.verifieer(); // stap 6 — ná `ready`, eenmalig per pageload
          return;
        case "RESIZE":
          this.planHoogte(bericht.payload.height);
          return;
        case "DESIGN_COMPLETE": {
          // `overige` EERST: de contractvelden staan er daarna overheen, zodat
          // een restpost met een sleutel die het contract wél kent er nooit
          // een kan overschrijven. Dat kan vandaag niet gebeuren (de parser
          // houdt die sleutels uit `overige`) en juist daarom is de volgorde
          // hier de goedkoopste plek om het ook structureel onmogelijk te
          // houden.
          const { overige, ...contract } = bericht.payload;
          const detail = { ...overige, ...contract } satisfies DesignCompleteDetail;
          if (this.isTweeling("DESIGN_COMPLETE", bericht.legacy, detail)) return;
          this.dispatch(SDK_EVENTS.DESIGN_COMPLETE, detail);
          return;
        }
        case "REQUEST_CLOSE":
          if (this.isTweeling("REQUEST_CLOSE", bericht.legacy, {})) return;
          this.dispatch(SDK_EVENTS.REQUEST_CLOSE, {});
          return;
        case "ADD_TO_CART":
          // Shopkoppelingen (WP K3b) — alleen als de pagina de winkelwagen
          // belooft (`cart="host"`). Zonder die belofte toont de configurator de
          // knop niet, dus een add-to-cart is dan ongevraagd en wordt genegeerd.
          // De payload is al gekeurd door de parser (`leesWinkelwagenToevoegen`).
          if (!this.heeftWinkelwagen()) return;
          this.dispatchAnnuleerbaar(SDK_EVENTS.ADD_TO_CART, bericht.payload);
          return;
        case "PLAATSING":
          this.verwerkPlaatsing(bericht.correlatie);
          return;
        case "INIT":
          // Loopt de andere kant op (parent → iframe). Een `init` die hier
          // binnenkomt is onze eigen echo of iemand die zich vergist.
          return;
      }
    }

    /**
     * §5.2 stap 5 — het antwoord op `ready`.
     *
     * ── WAAROM TWEE BERICHTEN ───────────────────────────────────────────────
     * Eén bericht draagt één `type`, en de runtime die vandaag LIVE staat
     * luistert uitsluitend naar `upgraded:init` (`INIT_MESSAGE` in
     * `embed-session-client.ts`). Zouden we alleen `m2f:init` sturen, dan
     * hangt elke bestaande embed tot de runtime mee is. Het contract schrijft
     * dat ook voor: *"een zender die beide kan zetten, zet beide"*.
     *
     * De nieuwe naam gaat EERST: een ontvanger die beide kent, settelt op de
     * eerste die hij herkent, en dat hoort de v1-naam te zijn. De ontvanger is
     * idempotent (`waitForEmbedInit` resolvet één keer), dus de tweede is
     * gratis.
     *
     * `targetOrigin` is het iframe-origin en niet `'*'`: dit bericht draagt de
     * api_key, en die hoort alleen bij ons eigen document aan te komen. Dat de
     * iframe-kant `'*'` moet gebruiken voor zijn `ready` is geen symmetrie —
     * die kant kent de klantorigin niet, wij kennen de onze wél.
     */
    /**
     * K05a — de poort op de READY van een element met `placement`. `true` =
     * de iframe zegt onze instance en plaatsing terug: door naar de INIT.
     * `false` = geen echo of een andere: dan is er al een `m2f:error` gemeld en
     * gaat er niets de deur uit, ook de sleutel niet.
     *
     * Een READY zonder onze instance-id komt van een runtime die `placement`
     * niet kent. Die zou de sessie buiten de plaatsing om starten, en precies
     * dat mag niet. (Een READY met een ÁNDERE instance-id komt hier niet: die
     * is in `verwerk` al stil genegeerd, want hij is voor een ander element.)
     */
    private keurReady(correlatie: EmbedCorrelatie | undefined): boolean {
      if (this.plaatsingStand !== "wacht") return this.plaatsingStand !== "geweigerd";
      if (
        correlatie?.instance_id !== this.eigenInstanceId ||
        correlatie?.plaatsing_id !== this.plaatsingId
      ) {
        this.weigerPlaatsing(
          SDK_FOUTCODES.PLAATSING_NIET_BESCHIKBAAR,
          "De embed bevestigt deze plaatsing niet.",
        );
        return false;
      }
      this.plaatsingStand = "verbonden";
      return true;
    }

    /**
     * K05a — het PLAATSING-bericht: de uitkomst van de sessie-uitwisseling,
     * na onze INIT. Een bevestiging telt alleen in de stand `verbonden` en
     * alleen één keer; een tweede (de andere spelling, of een herhaling)
     * verandert niets. Een bevestiging van een andere plaatsing dan de onze is
     * een weigering.
     *
     * Een WEIGERING telt ook in de stand `bevestigd`: een lopende sessie kan
     * alsnog stoppen, bijvoorbeeld als een verlenging geweigerd wordt
     * (capability of grant uit). De stand wordt dan `geweigerd`, met één
     * `m2f:error`; de tweede spelling valt daarna weg.
     */
    private verwerkPlaatsing(correlatie: EmbedCorrelatie | undefined): void {
      if (this.plaatsingId === null) return;
      if (
        correlatie?.plaatsing_weigering !== undefined &&
        (this.plaatsingStand === "verbonden" || this.plaatsingStand === "bevestigd")
      ) {
        this.weigerPlaatsing(correlatie.plaatsing_weigering, "De embed weigert deze plaatsing.");
        return;
      }
      if (this.plaatsingStand !== "verbonden") return;
      const versie = correlatie?.plaatsing_versie;
      if (correlatie?.plaatsing_id !== this.plaatsingId || versie === undefined) {
        this.weigerPlaatsing(
          SDK_FOUTCODES.PLAATSING_NIET_BESCHIKBAAR,
          "De embed bevestigt deze plaatsing niet.",
        );
        return;
      }
      this.plaatsingStand = "bevestigd";
      this.bevestigdeVersie = versie;
    }

    /** Sluit het handshake-venster zonder INIT, met één foutmelding. */
    private weigerPlaatsing(code: SdkFoutcode, bericht: string): void {
      this.plaatsingStand = "geweigerd";
      // Een sessie die stopt, heeft geen draaiende versie meer.
      this.bevestigdeVersie = null;
      // Zelfde reden als in `stuurInit`: de ready IS binnen, dus geen
      // `embed-init-timeout` meer achter deze melding aan.
      this.handshake = true;
      if (this.initTimer !== null) {
        clearTimeout(this.initTimer);
        this.initTimer = null;
      }
      this.meldFout(code, bericht);
    }

    private stuurInit(): void {
      if (this.initVerstuurd) return;

      // De `ready` IS binnen: het handshake-venster is hoe dan ook gesloten.
      // De timer eerst stoppen, want anders meldt een embed zonder key én gid
      // straks óók nog een `embed-init-timeout` — twee foutmeldingen voor één
      // probleem, en de tweede wijst naar de verkeerde oorzaak.
      this.handshake = true;
      if (this.initTimer !== null) {
        clearTimeout(this.initTimer);
        this.initTimer = null;
      }

      const venster = this.iframeEl?.contentWindow ?? null;
      if (venster === null) return;

      const key = (this.getAttribute("key") ?? "").trim();
      const gid = (this.getAttribute("gid") ?? "").trim();
      const modus: "key" | "cookie" = key !== "" ? "key" : "cookie";

      if (key === "" && gid === "") {
        this.meldFout(
          SDK_FOUTCODES.GEEN_SLEUTEL,
          `<${CUSTOM_ELEMENT_NAAM}> heeft \`key\` of \`gid\` nodig om een sessie te openen.`,
        );
        return;
      }

      // `parentOrigin` gaat mee omdat §5.2 stap 5 hem voorschrijft en het
      // portaal hem in de uitwisseling verwacht — maar de ONTVANGER hoort hem
      // te negeren en `event.origin` te gebruiken. Zie het docblok bij
      // `InitPayload`: dit veld is een bewering, geen feit.
      // K05a — met een plaatsing zegt de INIT de instance en de plaatsing
      // terug; de iframe neemt een INIT met een andere niet aan.
      const plaatsing =
        this.plaatsingStand === "verbonden" && this.plaatsingId !== null && this.eigenInstanceId !== null
          ? { instance_id: this.eigenInstanceId, plaatsing_id: this.plaatsingId }
          : {};
      // Shopkoppelingen (WP K3b) — `winkelwagen` alleen als de pagina de
      // winkelwagen belooft. Zonder `cart` gaat het init-bericht byte-gelijk
      // zoals vóór K3b: geen veld erbij.
      const winkel = this.winkelwagenBelofte();
      const lichaam = {
        ...(modus === "key" ? { key } : {}),
        mode: modus,
        parentOrigin: this.paginaOrigin(),
        ...plaatsing,
        ...(winkel === null ? {} : { winkelwagen: winkel }),
      };

      this.initVerstuurd = true;
      this.post(venster, { type: EMBED_BERICHTEN.INIT.m2f, ...lichaam });
      this.post(venster, { type: EMBED_BERICHTEN.INIT.legacy, ...lichaam });
    }

    private post(venster: Window, bericht: Record<string, unknown>): void {
      // K05a — nooit `'*'` voor een element met een plaatsing: dan liever
      // niets sturen. (Zonder plaatsing blijft de oude terugval staan; in de
      // praktijk is `iframeOrigin` nooit leeg, zie `leesOrigin`.)
      if (this.iframeOrigin === "" && this.plaatsingId !== null) return;
      try {
        venster.postMessage(bericht, this.iframeOrigin === "" ? "*" : this.iframeOrigin);
      } catch {
        // Een iframe die al weg is, een afgeschermde context, een browser die
        // het structured clone weigert: geen van drieën is iets wat de
        // klantpagina hoort te merken.
      }
    }

    /* ─── stap 6: de verificatie-ping ─────────────────────────────────── */

    /**
     * K33 — het portaal van DIT element: `portal="…"` als dat een http(s)-URL
     * is, anders de paginabrede waarde. `portal` staat bewust NIET in
     * `observedAttributes`: het wijzigen ervan hoeft de iframe niet te
     * herbouwen, en de ping gaat pas bij `ready`.
     */
    private portaalVoorElement(): string {
      const eigen = normaliseerBasis(this.getAttribute("portal") ?? "");
      return isHttpBasis(eigen) ? eigen : portaalBasis;
    }

    /**
     * §5.2 stap 6 — `POST /api/v1/embed/verify` met `{gid, origin}`, EENMALIG
     * per pageload, ná `ready`.
     *
     * Het portaal zet er `embed_verified_at` en `embed_last_origin` mee: de
     * koppelwizard kan daardoor laten zien dat de embed écht op de site van de
     * klant geladen is, en de embed-health-kaart ziet een site die het
     * probeert terwijl hij niet op de origin-whitelist staat.
     *
     * ── DIT IS EEN REGISTRATIE, GEEN POORT ──────────────────────────────────
     * Een mislukte ping mag de embed NIET blokkeren en doet dat hier ook niet:
     * er wordt niet op gewacht, het antwoord wordt niet gelezen, er komt geen
     * `m2f:error` van en er is geen herkansing. Offline, een adblocker die
     * `app.mark2form.com` tegenhoudt, CORS die klemt, een portaal dat 500
     * geeft — in alle gevallen draait de configurator gewoon door. Dat is geen
     * slordigheid maar de bedoeling: de bezoeker koopt een product, hij is
     * geen deelnemer aan onze administratie. Wat er bij zo'n mislukking
     * gebeurt, is dat het portaal geen tijdstempel krijgt; dat is zichtbaar in
     * de wizard en daar hoort het thuis.
     *
     * De sleutel gaat vóór de `fetch` in `GEVERIFIEERD` en niet erna. Een
     * mislukte ping wordt dus ook niet bij de volgende `ready` opnieuw
     * geprobeerd. Een herkansingslus op de pagina van een klant, voor een
     * bericht dat niemand leest, is erger dan een ontbrekende tijdstempel.
     *
     * ── ZONDER `gid` GEEN PING ──────────────────────────────────────────────
     * Het endpoint identificeert de embed op zijn grant-id en beantwoordt een
     * onbekende gid met 404. Een embed die alleen een `key` draagt heeft er
     * dus niets te melden, en een lege ping sturen zou alleen ruis in de
     * foutentellers van het portaal zijn.
     *
     * ── `origin` IN DE BODY IS EEN BEWERING ─────────────────────────────────
     * Net als `parentOrigin` in stap 5. De server leest de `Origin`-HEADER —
     * die zet de browser en een pagina kan hem niet vervalsen — en valt alleen
     * op dit veld terug bij `Origin: null` (een sandboxed iframe, een
     * `file://`-pagina). Het gaat mee omdat §5.2 het voorschrijft en het daar
     * een betere gok is dan niets, niet omdat het bewijs is.
     */
    private verifieer(): void {
      const gid = (this.getAttribute("gid") ?? "").trim();
      if (gid === "") return;

      const url = verifyUrl(this.portaalVoorElement());
      const sleutel = `${url}\u0000${gid}`;
      if (GEVERIFIEERD.has(sleutel)) return;
      GEVERIFIEERD.add(sleutel);

      // De `fetch` van het document waar dit element in hangt — niet de
      // globale: een element kan in een ander document leven. Bestaat hij
      // niet (een oude webview, een gestript testvenster), dan is stap 6
      // simpelweg niet beschikbaar en gaat de rest gewoon door.
      const venster = this.defaultView();
      if (venster === null || typeof venster.fetch !== "function") return;

      try {
        const antwoord = venster.fetch(url, {
          method: "POST",
          // Een cross-origin POST met `content-type: application/json` is een
          // niet-simpel verzoek en krijgt dus een preflight; het endpoint
          // beantwoordt `OPTIONS`. `credentials: "omit"` omdat er geen auth
          // is (§5.3: public-browser, geen auth) — cookies meesturen naar een
          // ander origin levert niets op en vraagt alleen om een strengere
          // CORS-afspraak.
          mode: "cors",
          credentials: "omit",
          cache: "no-store",
          // Laat de ping overleven als de bezoeker meteen wegklikt.
          keepalive: true,
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ gid, origin: this.paginaOrigin() }),
        });
        // Geen `await`, geen `.then`: het antwoord doet er niet toe. De
        // `catch` is er alleen om een afgewezen promise niet als
        // "unhandled rejection" op de console van de klant te laten landen.
        void antwoord.catch(() => undefined);
      } catch {
        // Een `fetch` die synchroon gooit (een ongeldige URL na een
        // eigenzinnige `zetPortaalBasis`, een dichtgetimmerde CSP-connect-src):
        // ook dat is geen reden om de embed te hinderen.
      }
    }

    /* ─── stap 8: de hoogte ───────────────────────────────────────────── */

    /**
     * De klem zit in de parser (`klemEmbedHoogte`, grenzen uit contracts) en is
     * dus al toegepast; hier staat alleen de debounce en de DOM-schrijfactie.
     * `laatsteHoogte` voorkomt een stijlmutatie — en daarmee een layout — voor
     * een hoogte die al staat.
     */
    private planHoogte(hoogte: number): void {
      const geklemd = klemEmbedHoogte(hoogte);
      if (geklemd === this.laatsteHoogte) return;
      if (this.resizeTimer !== null) clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.resizeTimer = null;
        if (this.iframeEl === null) return;
        this.laatsteHoogte = geklemd;
        this.iframeEl.style.height = `${geklemd}px`;
      }, RESIZE_DEBOUNCE_MS);
    }

    /* ─── shopkoppelingen: de winkelwagen (WP K3b) ────────────────────── */

    /**
     * De winkelwagen-belofte van dit element: `"host"` als `cart="host"` op het
     * element staat, anders `null`. Een onbekende waarde telt als afwezig (zie
     * `WINKELWAGEN_MODI` in contracts: een nieuwere waarde mag er niet een
     * halve belofte van maken).
     */
    private winkelwagenBelofte(): "host" | null {
      const waarde = (this.getAttribute("cart") ?? "").trim();
      return isWinkelwagenModus(waarde) ? waarde : null;
    }

    private heeftWinkelwagen(): boolean {
      return this.winkelwagenBelofte() !== null;
    }

    /**
     * De uitkomst van de winkel terugsturen naar de iframe (`m2f:cart-result`).
     *
     * Nooit `'*'`: het antwoord hoort alleen bij de origin die in de INIT stond
     * (`iframeOrigin`). Zonder die origin gaat er niets weg. De inhoud wordt
     * eerst gekeurd en daarna NIEUW opgebouwd uit de bekende velden: wat de
     * pagina meer meestuurt, komt niet op de draad.
     */
    winkelwagenUitkomst(uitkomst: unknown): boolean {
      const bericht = geldigeWinkelwagenUitkomst(uitkomst);
      if (bericht === null) {
        this.meldFout(
          FOUT_ONGELDIG_CART_RESULT,
          "winkelwagenUitkomst() kreeg een antwoord dat niet klopt met het contract (WinkelwagenUitkomst): de iframe krijgt er niets van.",
        );
        return false;
      }
      const venster = this.iframeEl?.contentWindow ?? null;
      if (venster === null || this.iframeOrigin === "") return false;
      try {
        venster.postMessage({ type: EMBED_BERICHTEN.CART_RESULT.m2f, ...bericht }, this.iframeOrigin);
        return true;
      } catch {
        // Een iframe die al weg is: geen reden om de pagina te laten crashen.
        return false;
      }
    }

    /* ─── stap 9 en de foutmelding ────────────────────────────────────── */

    /**
     * Is dit stap-9-bericht de tweeling van het vorige (andere spelling,
     * gelijke payload, binnen `TWEELING_VENSTER_MS`)? Dan `true`, en het spoor
     * is daarmee verbruikt. Zo niet, dan wordt dit bericht het nieuwe spoor en
     * hoort de aanroeper het af te leveren. Zie `TWEELING_VENSTER_MS` voor het
     * waarom.
     */
    private isTweeling(soort: Stap9Soort, legacy: boolean, payload: unknown): boolean {
      const vingerafdruk = vingerafdrukVan(payload);
      const nu = Date.now();
      const vorige = this.stap9Spoor.get(soort);
      if (
        vorige !== undefined &&
        vingerafdruk !== null &&
        vorige.legacy !== legacy &&
        vorige.vingerafdruk === vingerafdruk &&
        nu - vorige.tijd <= TWEELING_VENSTER_MS
      ) {
        this.stap9Spoor.delete(soort);
        return true;
      }
      if (vingerafdruk === null) this.stap9Spoor.delete(soort);
      else this.stap9Spoor.set(soort, { legacy, vingerafdruk, tijd: nu });
      return false;
    }

    private dispatch(naam: string, detail: unknown): void {
      this.dispatchEvent(
        new CustomEvent(naam, { detail, bubbles: true, composed: true, cancelable: false }),
      );
    }

    /** Als `dispatch`, maar annuleerbaar: `m2f:add-to-cart` mag de pagina zelf afhandelen. */
    private dispatchAnnuleerbaar(naam: string, detail: unknown): void {
      this.dispatchEvent(
        new CustomEvent(naam, { detail, bubbles: true, composed: true, cancelable: true }),
      );
    }

    /**
     * M16 — naast het event ook één regel in de console, met de hulpcode uit
     * het contract (`EMBED_HULPCODES`): die legt het portaal uit bij het
     * websiteblok ("Werkt het niet?"). `FoutDetail` blijft gelijk.
     */
    private meldFout(code: SdkFoutcode, bericht: string): void {
      const hulp = embedHulpcode(code);
      try {
        console.warn(`[${CUSTOM_ELEMENT_NAAM}] ${hulp} (${code}): ${bericht}`);
      } catch {
        /* console kan ontbreken */
      }
      this.dispatch(SDK_EVENTS.FOUT, { code, bericht } satisfies FoutDetail);
    }

    /* ─── opruimen ────────────────────────────────────────────────────── */

    private ruimOp(): void {
      if (this.onBericht !== null) {
        this.defaultView()?.removeEventListener("message", this.onBericht);
        this.onBericht = null;
      }
      if (this.onLoad !== null && this.iframeEl !== null) {
        this.iframeEl.removeEventListener("load", this.onLoad);
      }
      this.onLoad = null;
      if (this.initTimer !== null) clearTimeout(this.initTimer);
      if (this.resizeTimer !== null) clearTimeout(this.resizeTimer);
      this.initTimer = null;
      this.resizeTimer = null;
      if (this.iframeEl !== null && this.iframeEl.parentNode === this) {
        this.removeChild(this.iframeEl);
      }
      this.iframeEl = null;
      this.handshake = false;
      this.initVerstuurd = false;
      this.laatsteHoogte = 0;
      this.iframeOrigin = "";
      // Een nieuwe iframe is een nieuwe zender: geen tweeling van de vorige.
      this.stap9Spoor.clear();
      // K05a — en een nieuwe instance: de volgende `bouw()` maakt een verse id,
      // dus een laat bericht van deze iframe past daarna nergens meer op.
      this.plaatsingId = null;
      this.eigenInstanceId = null;
      this.plaatsingStand = "wacht";
      this.bevestigdeVersie = null;
    }

    /** Het venster van het document waar dit element in hangt (niet `window`:
     *  een element kan in een ander document leven, bv. een print-preview). */
    private defaultView(): Window | null {
      return this.ownerDocument.defaultView;
    }

    /** Het origin van de KLANTPAGINA — wat er als `parentOrigin` meegaat. */
    private paginaOrigin(): string {
      try {
        return this.defaultView()?.location.origin ?? "";
      } catch {
        return "";
      }
    }
  }

  klasseCache = Mark2FormConfigurator as unknown as Mark2FormConfiguratorConstructor;
  return klasseCache;
}

/* ══════════════════════════════════════════════════════════════════════════
   REGISTRATIE (§5.2 stap 2)
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Registreert `<mark2form-configurator>` (naam uit contracts).
 *
 * Geeft `false` terug als er al iets onder die naam staat — twee keer
 * `customElements.define` met dezelfde naam GOOIT, en een klant die de bundel
 * per ongeluk twee keer insluit (een thema plus een plugin, dat gebeurt) hoort
 * geen rode pagina te krijgen maar één werkende embed.
 */
export function registreerCustomElement(): boolean {
  if (typeof customElements === "undefined") return false;
  if (customElements.get(CUSTOM_ELEMENT_NAAM) !== undefined) return false;
  customElements.define(CUSTOM_ELEMENT_NAAM, maakConfiguratorElementKlasse());
  return true;
}

/** Alleen voor tests: vergeet de gecachete klasse. */
export function _resetKlasseCache(): void {
  klasseCache = null;
}

/**
 * Alleen voor tests: doe alsof dit een verse pageload is.
 *
 * `GEVERIFIEERD` leeft op module-niveau en wordt in een browser alleen door een
 * pageload gewist — precies wat §5.2 stap 6 vraagt. In een testrun leeft de
 * module langer dan één "pagina", dus daar moet het met de hand.
 */
export function _resetVerificatieCache(): void {
  GEVERIFIEERD.clear();
}

/**
 * K05a — een instance-id: 16 willekeurige bytes als 32 hextekens (valt binnen
 * `PLAATSING_INSTANCE_ID_PATROON`). `getRandomValues` en niet `randomUUID`:
 * de tweede bestaat alleen in een beveiligde context, en er zijn klantsites op
 * `http:`. `null` als er geen `crypto` is.
 */
function maakInstanceId(venster: Window | null): string | null {
  const bron: Crypto | undefined =
    venster?.crypto ?? (typeof crypto === "undefined" ? undefined : crypto);
  if (bron === undefined || typeof bron.getRandomValues !== "function") return null;
  const bytes = bron.getRandomValues(new Uint8Array(16));
  let uit = "";
  for (const byte of bytes) uit += byte.toString(16).padStart(2, "0");
  return uit;
}

/**
 * Het origin uit een basis-URL. Faalt de parse (een relatief pad in een
 * `base`-attribuut), dan `""` — en dan valt de origin-check terug op de
 * `event.source`-check alleen. Liever een embed die werkt met één controle dan
 * een embed die niets doet omdat iemand een schuine streep vergat.
 */
function leesOrigin(basis: string): string {
  try {
    return new URL(basis).origin;
  } catch {
    return "";
  }
}

/* ══════════════════════════════════════════════════════════════════════════
   HET ANTWOORD VAN DE WINKEL — de zod-vrije controle (WP K3b)
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * De zod-vrije spiegel van `WinkelwagenUitkomstSchema` (contracts/winkelwagen).
 * Deze module gaat in `sdk/v1.js` en mag geen zod bevatten. Een test in
 * `__tests__/winkelwagen.test.ts` vergelijkt deze controle met het schema op een
 * reeks voorbeelden, zodat de twee niet uit elkaar kunnen lopen.
 *
 * Geeft de schone uitkomst terug (alleen de bekende velden) of `null`. Een
 * ontbrekend veld faalt vanzelf: `undefined` is noch een string, noch `null`.
 */
function geldigeWinkelwagenUitkomst(waarde: unknown): Record<string, unknown> | null {
  if (typeof waarde !== "object" || waarde === null || Array.isArray(waarde)) return null;
  const r = waarde as Record<string, unknown>;

  if (r.ok === true) {
    if (!alleenSleutels(r, ["ok", "offerte_id", "winkelwagen_url", "aantal_in_winkelwagen"])) return null;
    const url = r.winkelwagen_url;
    const aantal = r.aantal_in_winkelwagen;
    if (!isOfferteId(r.offerte_id)) return null;
    if (!(url === null || (typeof url === "string" && isAbsoluteUrl(url)))) return null;
    if (!(aantal === null || (typeof aantal === "number" && Number.isInteger(aantal) && aantal >= 0 && aantal <= 10000))) {
      return null;
    }
    return { ok: true, offerte_id: r.offerte_id, winkelwagen_url: url, aantal_in_winkelwagen: aantal };
  }

  if (r.ok === false) {
    if (!alleenSleutels(r, ["ok", "offerte_id", "code", "melding"])) return null;
    const code = r.code;
    const melding = r.melding;
    if (!isOfferteId(r.offerte_id)) return null;
    if (typeof code !== "string" || !(WINKELWAGEN_FOUTCODES as readonly string[]).includes(code)) return null;
    if (!(melding === null || (typeof melding === "string" && melding.length <= WINKELWAGEN_MELDING_MAX))) return null;
    return { ok: false, offerte_id: r.offerte_id, code, melding };
  }

  return null;
}

/** `strict()` uit zod: geen sleutel buiten de toegestane lijst. */
function alleenSleutels(waarde: Record<string, unknown>, toegestaan: readonly string[]): boolean {
  return Object.keys(waarde).every((sleutel) => toegestaan.includes(sleutel));
}

/** De zod-vrije tegenhanger van `z.string().url()`: een adres dat `URL` kan lezen. */
function isAbsoluteUrl(waarde: string): boolean {
  try {
    new URL(waarde);
    return true;
  } catch {
    return false;
  }
}
